import React from 'react';
import {
  Box, Button, Divider, MenuItem, MenuList, Popover, Stack, Typography, useMediaQuery,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import CalendarMonthRounded from '@mui/icons-material/CalendarMonthRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateField } from '@mui/x-date-pickers/DateField';
import dayjs, { Dayjs } from 'dayjs';
import { RangeCalendar, type RangeSelection } from './RangeCalendar';
import {
  DEFAULT_DATE_PRESETS,
  ISO_DAY,
  describeSpan,
  formatDateRange,
  toRange,
  type DateRange,
  type DateRangePreset,
  type WeekStart,
} from '../utils/dateRange';

export interface DateRangePickerProps {
  /** Both ends inclusive, YYYY-MM-DD. */
  value: DateRange;
  /** `preset` is the preset that produced the range, or null for a custom one. */
  onChange: (next: DateRange, preset: DateRangePreset | null) => void;
  /** Shortcuts down the left. Defaults to `DEFAULT_DATE_PRESETS`; `[]` hides the column. */
  presets?: DateRangePreset[];
  /** Offer the calendar for a custom range. Off, the picker is presets only. */
  allowCustom?: boolean;
  minDate?: string;
  maxDate?: string;
  /** The day presets count back from. Defaults to the real today. */
  today?: string;
  weekStartsOn?: WeekStart;
  /** How the button reads: `day` "4 Aug – 31 Aug 2026", `month` "Aug 2025 – Aug 2026". */
  display?: 'day' | 'month';
  /** Lead with the preset's name when the range matches one: "Last month · Aug 2026". */
  showPresetLabel?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';
  /** Names the control for screen readers. */
  ariaLabel?: string;
}

const firstShownMonth = (iso: string, months: number, panel: number = months - 1) =>
  dayjs(iso).startOf('month').subtract(panel, 'month').format(ISO_DAY);

/**
 * The app's date range control: a button showing the range, opening presets
 * beside a calendar.
 *
 * A preset applies at once. A custom range is picked on the calendar or typed
 * into the Start and End fields, then applied — two clicks make a range, and
 * it should not change the page after the first.
 *
 * Configurable for pages that need less: pass the presets that make sense
 * (`DATE_PRESETS`, `lastFullMonths`, `lastDays`, or your own), `[]` for none,
 * or `allowCustom={false}` for presets only.
 *
 * Not for a single date — use MUI's DatePicker for that.
 */
export function DateRangePicker({
  value,
  onChange,
  presets = DEFAULT_DATE_PRESETS,
  allowCustom = true,
  minDate,
  maxDate,
  today,
  weekStartsOn = 0,
  display = 'day',
  showPresetLabel = false,
  disabled,
  size = 'small',
  ariaLabel = 'Date range',
}: DateRangePickerProps) {
  const narrow = useMediaQuery((t: Theme) => t.breakpoints.down('sm'));
  const months = narrow ? 1 : 2;
  const todayDay = React.useMemo(() => (today ? dayjs(today) : dayjs()).startOf('day'), [today]);

  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const [draft, setDraft] = React.useState<RangeSelection>(value);
  const [month, setMonth] = React.useState(() => firstShownMonth(value.end, months));
  // The typed fields keep their own state: while someone is half way through
  // typing a date it is invalid, and it must not be thrown away.
  const [fieldStart, setFieldStart] = React.useState<Dayjs | null>(dayjs(value.start));
  const [fieldEnd, setFieldEnd] = React.useState<Dayjs | null>(dayjs(value.end));

  // A preset may reach past the dates the page allows, e.g. "Last year" on a
  // board whose history starts in March. It is trimmed rather than refused.
  const presetRange = React.useCallback(
    (preset: DateRangePreset): DateRange => {
      let { start, end } = preset.resolve(todayDay, weekStartsOn);
      if (minDate && start.isBefore(minDate, 'day')) start = dayjs(minDate);
      if (maxDate && end.isAfter(maxDate, 'day')) end = dayjs(maxDate);
      return toRange(start, end);
    },
    [todayDay, weekStartsOn, minDate, maxDate],
  );

  const presetFor = React.useCallback(
    (range: RangeSelection) =>
      presets.find((p) => {
        const r = presetRange(p);
        return r.start === range.start && r.end === range.end;
      }) ?? null,
    [presets, presetRange],
  );

  const openPopover = (event: React.MouseEvent<HTMLElement>) => {
    setDraft(value);
    setFieldStart(dayjs(value.start));
    setFieldEnd(dayjs(value.end));
    setMonth(firstShownMonth(value.end, months));
    setAnchor(event.currentTarget);
  };
  const close = () => setAnchor(null);

  const commit = (range: DateRange) => {
    onChange(range, presetFor(range));
    close();
  };

  const onCalendarChange = (next: RangeSelection) => {
    setDraft(next);
    setFieldStart(next.start ? dayjs(next.start) : null);
    setFieldEnd(next.end ? dayjs(next.end) : null);
  };

  const onFieldChange = (which: 'start' | 'end') => (typed: Dayjs | null) => {
    (which === 'start' ? setFieldStart : setFieldEnd)(typed);
    if (typed && typed.isValid() && typed.year() >= 1000) {
      const iso = typed.format(ISO_DAY);
      setDraft((current) => ({ ...current, [which]: iso }));
      setMonth(firstShownMonth(iso, months, which === 'start' ? 0 : months - 1));
    }
  };

  const within = (iso: string) => (!minDate || iso >= minDate) && (!maxDate || iso <= maxDate);
  const ready =
    !!draft.start && !!draft.end && draft.start <= draft.end && within(draft.start) && within(draft.end);
  const activePreset = ready ? presetFor(draft) : null;

  const shown = formatDateRange(value, display);
  const matched = showPresetLabel ? presetFor(value) : null;

  return (
    <>
      <Button
        variant="outlined"
        color="inherit"
        size={size}
        disabled={disabled}
        onClick={openPopover}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
        aria-label={`${ariaLabel}: ${shown}`}
        startIcon={<CalendarMonthRounded />}
        endIcon={<ExpandMoreRounded />}
        sx={(t) => ({
          minHeight: size === 'small' ? t.ds.sizes.control.sm : t.ds.sizes.control.md,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
          color: t.tokens.text.primary,
          bgcolor: t.tokens.surface.raised,
          borderColor: t.tokens.border.default,
          '& .MuiButton-startIcon, & .MuiButton-endIcon': { color: t.tokens.text.secondary },
          '&:hover': { borderColor: t.tokens.border.strong, bgcolor: t.tokens.surface.raised },
        })}
      >
        {matched ? `${matched.label} · ${shown}` : shown}
      </Button>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-label': ariaLabel,
            sx: (t: Theme) => ({
              mt: 0.75,
              maxWidth: 'calc(100vw - 32px)',
              bgcolor: t.tokens.surface.overlay,
              border: `1px solid ${t.tokens.border.subtle}`,
              borderRadius: `${t.ds.radius.md}px`,
              boxShadow: t.tokens.shadow.md,
            }),
          },
        }}
      >
        <Stack direction={narrow ? 'column' : 'row'}>
          {presets.length > 0 && (
            <Box
              sx={(t) => ({
                width: narrow || !allowCustom ? 'auto' : 188,
                minWidth: allowCustom ? undefined : 200,
                maxHeight: narrow && allowCustom ? 200 : undefined,
                overflowY: 'auto',
                py: 1,
                ...(allowCustom && (narrow
                  ? { borderBottom: `1px solid ${t.tokens.border.subtle}` }
                  : { borderRight: `1px solid ${t.tokens.border.subtle}` })),
              })}
            >
              <MenuList dense aria-label="Presets" sx={{ py: 0 }}>
                {presets.map((preset) => (
                  <MenuItem
                    key={preset.id}
                    selected={activePreset?.id === preset.id}
                    onClick={() => commit(presetRange(preset))}
                  >
                    <Stack sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap>{preset.label}</Typography>
                      {preset.description && (
                        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                          {preset.description}
                        </Typography>
                      )}
                    </Stack>
                  </MenuItem>
                ))}
              </MenuList>
            </Box>
          )}

          {allowCustom && (
            <Stack spacing={1.5} sx={{ p: 2, minWidth: 0 }}>
              {/* No app-level LocalizationProvider exists, so the picker brings
                  its own; the typed fields throw on mount without one. */}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Stack direction="row" spacing={1}>
                  <DateField
                    label="Start"
                    size="small"
                    fullWidth
                    format="D MMM YYYY"
                    value={fieldStart}
                    onChange={onFieldChange('start')}
                    minDate={minDate ? dayjs(minDate) : undefined}
                    maxDate={maxDate ? dayjs(maxDate) : undefined}
                  />
                  <DateField
                    label="End"
                    size="small"
                    fullWidth
                    format="D MMM YYYY"
                    value={fieldEnd}
                    onChange={onFieldChange('end')}
                    minDate={draft.start ? dayjs(draft.start) : minDate ? dayjs(minDate) : undefined}
                    maxDate={maxDate ? dayjs(maxDate) : undefined}
                  />
                </Stack>
              </LocalizationProvider>

              <RangeCalendar
                value={draft}
                onChange={onCalendarChange}
                month={month}
                onMonthChange={setMonth}
                months={months}
                minDate={minDate}
                maxDate={maxDate}
                weekStartsOn={weekStartsOn}
                today={todayDay.format(ISO_DAY)}
              />

              <Divider />

              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  variant="caption"
                  sx={(t) => ({ flex: 1, minWidth: 0, color: t.tokens.text.secondary, fontVariantNumeric: 'tabular-nums' })}
                >
                  {ready
                    ? `${formatDateRange(draft as DateRange)} · ${describeSpan(draft as DateRange)}`
                    : draft.start
                      ? 'Now pick the last day'
                      : 'Pick the first day'}
                </Typography>
                <Button size="small" color="inherit" onClick={close}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!ready}
                  onClick={() => ready && commit(draft as DateRange)}
                >
                  Apply
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Popover>
    </>
  );
}
